import React, { useMemo, useState, useRef } from "react";
import { TrashIcon, XMarkIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, DocumentCheckIcon, PrinterIcon, PencilIcon, ExclamationTriangleIcon, UserIcon, SparklesIcon, TicketIcon, FaceSmileIcon, WindowIcon } from "@heroicons/react/24/outline";
import AdultToothChart from "./AdultToothChart";
import ChildrenToothChart from "./ChildrenToothChart";
import DevisDocument from "./DevisDocument"; 

const normalize = (str = "") =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const DevisGenerator = ({ soins, groupOrder = [] }) => {
  const [patientName, setPatientName] = useState("");
  const [date, setDate] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const [discountMode, setDiscountMode] = useState("amount");
  const [discountValue, setDiscountValue] = useState(0);
  const [items, setItems] = useState([]);
  const [activeLineId, setActiveLineId] = useState(null);
  const [currentTeeth, setCurrentTeeth] = useState([]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLignesCollapsed, setIsLignesCollapsed] = useState(false);
  const [toast, setToast] = useState(null);
  const [isLignesModalOpen, setIsLignesModalOpen] = useState(false);

  const activeLineRef = useRef(null);

  // Groups
  const groups = useMemo(() => {
    const groupSet = new Set();
    soins.forEach(s => {
      const soinGroups = Array.isArray(s.groups) ? s.groups : (s.group ? [s.group] : []);
      soinGroups.forEach(g => groupSet.add(g));
    });
    if (groupSet.size === 0) groupSet.add("Sans groupe");
    
    // Respect catalog order if groupOrder provided
    if (groupOrder && groupOrder.length > 0) {
      const orderedGroups = groupOrder.filter(g => groupSet.has(g));
      // Add any groups not in groupOrder (in case of new groups)
      Array.from(groupSet).forEach(g => {
        if (!orderedGroups.includes(g)) {
          orderedGroups.push(g);
        }
      });
      return orderedGroups;
    }
    
    return Array.from(groupSet).sort();
  }, [soins, groupOrder]);

  // Soins in expanded group
  const soinsInExpandedGroup = useMemo(() => {
    if (!expandedGroup) return [];
    return soins.filter(s => {
      const soinGroups = Array.isArray(s.groups) ? s.groups : (s.group ? [s.group] : []);
      return soinGroups.includes(expandedGroup);
    });
  }, [expandedGroup, soins]);

  // Filtered soins for global search
  const filteredSoins = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = normalize(searchQuery);
    return soins
      .filter(s => normalize(s.code).includes(q) || normalize(s.name).includes(q))
      .slice(0, 10);
  }, [soins, searchQuery]);

  // Don't scroll automatically - removed scroll behavior
  // useEffect(() => {
  //   if (activeLineId && activeLineRef.current) {
  //     activeLineRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  //   }
  // }, [activeLineId]);

  const handlePriceChange = (id, value) => {
    const price = value === "" ? "" : Number(value);
    if (price !== "" && (Number.isNaN(price) || price < 0)) return;
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, price } : item))
    );
  };

  const resetPrice = (id) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, price: item.soinDefaultPrice } : item))
    );
  };

  const removeLine = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    if (activeLineId === id) setActiveLineId(null);
  };

  // Toggle validation state of a line
  const toggleValidateLine = (id) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== id) return item;
        const newValidated = !item.validated;
        if (newValidated && activeLineId === id) {
          setActiveLineId(null);
        }
        return { ...item, validated: newValidated };
      })
    );
  };

  // Create a new line with given soin and current teeth (unvalidated)
  const addSoinLine = (soin) => {
    // If there's an active line, update it instead of adding a new one
    if (activeLineId) {
      const activeLine = items.find(i => i.id === activeLineId);
      if (activeLine && !activeLine.validated) {
        setItems(prev =>
          prev.map(item =>
            item.id === activeLineId
              ? {
                  ...item,
                  soinCode: soin.code,
                  soinName: soin.name,
                  price: soin.defaultPrice,
                  soinDefaultPrice: soin.defaultPrice,
                }
              : item
          )
        );
        setSearchQuery("");
        setShowSearchResults(false);
        
        // Show toast notification
        setToast(`Ligne mise à jour avec "${soin.name}"`);
        setTimeout(() => setToast(null), 2000);
        return;
      }
    }
    
    // Otherwise, add a new line
    const newId = items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 1;
    const newItem = {
      id: newId,
      soinCode: soin.code,
      soinName: soin.name,
      price: soin.defaultPrice,
      soinDefaultPrice: soin.defaultPrice,
      toothCode: currentTeeth.join(', '),
      validated: false,
    };
    setItems(prev => [...prev, newItem]);
    setSearchQuery("");
    setShowSearchResults(false);
    
    // Show toast notification
    setToast(`Ligne "${soin.name}" ajoutée`);
    setTimeout(() => setToast(null), 2000);
  };

  // Tooth selection handler – NO automatic line creation
  const handleToothSelection = (selectedTeeth) => {
    setCurrentTeeth(selectedTeeth);

    if (activeLineId) {
      const activeLine = items.find(i => i.id === activeLineId);
      if (activeLine && !activeLine.validated) {
        setItems(prev =>
          prev.map(item =>
            item.id === activeLineId
              ? { ...item, toothCode: selectedTeeth.join(', ') }
              : item
          )
        );
        return;
      }
    }
  };

  // Activate a line (only if not validated)
  const activateLine = (id) => {
    const line = items.find(i => i.id === id);
    if (!line || line.validated) return;
    
    // If clicking the same line again, deselect it
    if (activeLineId === id) {
      setActiveLineId(null);
      setCurrentTeeth([]);
      return;
    }
    
    // Get the soin to find its groups
    const soin = soins.find(s => s.code === line.soinCode);
    const soinGroups = soin && Array.isArray(soin.groups) ? soin.groups : (soin?.group ? [soin.group] : []);
    
    // Find the first group (in order) that contains this soin
    let groupToExpand = null;
    for (const group of groups) {
      if (soinGroups.includes(group)) {
        groupToExpand = group;
        break;
      }
    }
    
    // If still no group found, expand the first group that has any soins
    if (!groupToExpand) {
      groupToExpand = groups.find(group =>
        soins.some(s => {
          const sGroups = Array.isArray(s.groups) ? s.groups : (s.group ? [s.group] : []);
          return sGroups.includes(group);
        })
      );
    }
    
    if (groupToExpand) {
      setExpandedGroup(groupToExpand);
    }
    
    setCurrentTeeth(line.toothCode ? line.toothCode.split(',').map(t => t.trim()).filter(Boolean) : []);
    setActiveLineId(id);
  };

  // Remove a specific tooth from a line (chip removal) – only allowed if line is not validated
  const removeToothFromLine = (lineId, toothToRemove) => {
    const line = items.find(i => i.id === lineId);
    if (line?.validated) return;

    setItems(prev =>
      prev.map(item => {
        if (item.id !== lineId) return item;
        const currentTeethArr = item.toothCode
          ? item.toothCode.split(',').map(t => t.trim()).filter(Boolean)
          : [];
        const newTeeth = currentTeethArr.filter(t => t !== toothToRemove);
        if (lineId === activeLineId) {
          setCurrentTeeth(newTeeth);
        }
        return { ...item, toothCode: newTeeth.join(', ') };
      })
    );
  };

  const generatePdfFilename = () => {
    const dateObj = new Date(date + "T00:00:00");
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const formattedDate = `${year}${month}${day}`;
    const normalizedName = patientName
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "_");
    return `${formattedDate}_${normalizedName || "patient"}.pdf`;
  };

  const handlePrint = () => setIsModalOpen(true);

  const subtotal = items.reduce((sum, item) => {
    const price = item.price === "" || item.price == null ? 0 : Number(item.price);
    if (Number.isNaN(price)) return sum;
    return sum + price;
  }, 0);

  const discountAmount =
    discountMode === "percent"
      ? (subtotal * discountValue) / 100
      : discountValue;

  const totalAfterDiscount = subtotal - discountAmount;

  const getStatus = () => {
    if (activeLineId) {
      const line = items.find(i => i.id === activeLineId);
      if (!line?.soinCode) return { color: 'amber', message: 'Ligne active – sélectionnez un soin' };
      if (!line?.toothCode) return { color: 'amber', message: 'Ligne active – sélectionnez des dents' };
      return { color: 'blue', message: 'Ligne active – vous pouvez modifier' };
    }
    if (items.length === 0) return { color: 'amber', message: 'Sélectionnez des dents, puis cliquez sur un soin pour créer une ligne' };
    return { color: 'green', message: 'Prêt pour impression – cliquez sur une ligne non validée pour modifier' };
  };

  const status = getStatus();

  const sortedItems = useMemo(() => {
    return items;
  }, [items]);

  return (
    <div className="space-y-6">
      <style>{`
        @media print {
          body, html {
            margin: 0;
            padding: 0;
            width: 100%;
            overflow: hidden;
          }
          .modal-overlay {
            display: none !important;
          }
          .print-modal {
            position: static !important;
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .print-modal-header {
            display: none !important;
          }
          .print-modal-footer {
            display: none !important;
          }
          .print-modal-container {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
          }
          .print-modal-container::-webkit-scrollbar {
            display: none !important;
          }
          .print-area {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            position: static;
          }
        }
      `}</style>
      {/* Header Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl shadow-lg p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Générateur de devis</h1>
        <p className="text-indigo-100">Créez des devis professionnels pour vos patients</p>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left column – Patient info, soins selection, and items list */}
        <div className="lg:col-span-2 space-y-5">
          {/* Patient Info Card */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <UserIcon className="w-6 h-6" />
              Informations du patient
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nom et prénom
                </label>
                <input
                  type="text"
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  placeholder="Ex: Ahmed Alaoui"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Date du devis
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>

          {/* Soins Selection Card */}
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl shadow-md border-2 border-gray-200 p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <SparklesIcon className="w-6 h-6" />
              Sélectionner les soins
            </h2>

            {/* Search Box */}
            <div className="mb-4 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(e.target.value.trim().length > 0);
                }}
                onFocus={() => setShowSearchResults(searchQuery.trim().length > 0)}
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                placeholder="Chercher un soin..."
              />
              {showSearchResults && filteredSoins.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-blue-200 rounded-lg shadow-lg z-20 max-h-56 overflow-y-auto">
                  {filteredSoins.map(soin => (
                    <button
                      key={soin.code}
                      onClick={() => {
                        addSoinLine(soin);
                        setShowSearchResults(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors text-sm"
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{soin.name}</p>
                          <p className="text-xs text-gray-500 font-mono">{soin.code}</p>
                        </div>
                        <span className="text-xs font-bold text-blue-600 whitespace-nowrap">{soin.defaultPrice} DH</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Groups Accordion */}
            <div className="space-y-1">
              {groups.map(group => (
                <div key={group} className="border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedGroup(expandedGroup === group ? null : group)}
                    className="w-full px-3 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors flex items-center justify-between font-semibold text-gray-800 text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-500 text-white text-xs rounded-full font-bold">
                        {soins.filter(s => {
                          const soinGroups = Array.isArray(s.groups) ? s.groups : (s.group ? [s.group] : []);
                          return soinGroups.includes(group);
                        }).length}
                      </span>
                      {group}
                    </span>
                    <span className={`transform transition-transform text-xs ${expandedGroup === group ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>
                  {expandedGroup === group && (
                    <div className="bg-white p-3">
                      <div className="grid grid-cols-3 gap-2">
                        {soinsInExpandedGroup.map(soin => {
                          const activeLine = items.find(i => i.id === activeLineId);
                          const isSelected = activeLine && activeLine.soinCode === soin.code;
                          return (
                            <button
                              key={soin.code}
                              onClick={() => addSoinLine(soin)}
                              className={`text-left px-2.5 py-2.5 border rounded-lg transition-all duration-200 group ${
                                isSelected
                                  ? 'bg-blue-200 border-blue-500 hover:bg-blue-300'
                                  : 'bg-gray-50 border-gray-200 hover:bg-blue-100 hover:border-blue-400'
                              } hover:shadow-sm`}
                              title={`${soin.name} - ${soin.defaultPrice} DH`}
                            >
                              <p className={`font-semibold text-sm transition-colors line-clamp-4 ${
                                isSelected
                                  ? 'text-blue-900'
                                  : 'text-gray-900 group-hover:text-blue-700'
                              }`}>{soin.name}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Discount Card */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TicketIcon className="w-6 h-6" />
              Remise
            </h2>
            <div className="space-y-4">
              <div className="flex gap-2">
                <label className="flex-1 flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="percent"
                    checked={discountMode === "percent"}
                    onChange={(e) => setDiscountMode(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">Pourcentage (%)</span>
                </label>
                <label className="flex-1 flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="amount"
                    checked={discountMode === "amount"}
                    onChange={(e) => setDiscountMode(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">Montant (DH)</span>
                </label>
              </div>
              <input
                type="number"
                min="0"
                value={discountValue === 0 ? "" : discountValue}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    setDiscountValue(0);
                  } else {
                    const num = Number(val);
                    if (!isNaN(num) && num >= 0) {
                      setDiscountValue(num);
                    }
                  }
                }}
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                placeholder="0"
              />
            </div>
          </div>

          {/* Summary Card */}
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border-2 border-indigo-200 p-5">
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-700">Sous-total:</span>
                <span className="font-semibold text-gray-900">{subtotal.toFixed(2)} DH</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between items-center text-sm text-green-600">
                  <span>Remise:</span>
                  <span className="font-semibold">- {discountAmount.toFixed(2)} DH</span>
                </div>
              )}
              <div className="border-t-2 border-indigo-300 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total TTC:</span>
                  <span className="text-xl font-black text-indigo-600">{totalAfterDiscount.toFixed(2)} DH</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right column – Items list, soins groups, and tooth charts */}
        <div className="lg:col-span-2 space-y-5">
          {/* Items List Card */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => setIsLignesCollapsed(!isLignesCollapsed)}
                className="flex-1 flex items-center gap-3 text-lg font-bold text-gray-900 hover:text-indigo-600 transition-colors"
              >
                <DocumentCheckIcon className="w-6 h-6" />
                <span>Lignes du devis</span>
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">
                  {items.length}
                </span>
                <div className="ml-auto">
                  {isLignesCollapsed ? (
                    <ChevronDownIcon className="w-5 h-5" />
                  ) : (
                    <ChevronUpIcon className="w-5 h-5" />
                  )}
                </div>
              </button>
              {items.length > 0 && (
                <button
                  onClick={() => setIsLignesModalOpen(true)}
                  className="p-2 hover:bg-indigo-100 rounded-lg transition-colors"
                  title="Vue détaillée"
                >
                  <WindowIcon className="w-5 h-5 text-indigo-600" />
                </button>
              )}
            </div>
            
            {!isLignesCollapsed && (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {sortedItems.map((item, index) => {
                const isEditable = item.id === activeLineId && !item.validated;
                const teethArray = item.toothCode
                  ? item.toothCode.split(',').map(t => t.trim()).filter(Boolean)
                  : [];

                return (
                  <div
                    key={item.id}
                    ref={item.id === activeLineId ? activeLineRef : null}
                    onClick={() => activateLine(item.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                      item.validated
                        ? 'bg-green-50 border-green-300'
                        : isEditable
                        ? 'bg-indigo-50 border-indigo-400 shadow-md'
                        : 'bg-gray-50 border-gray-300 hover:border-indigo-300'
                    }`}
                  >
                    <div className="grid grid-cols-12 gap-3 mb-2">
                      <div className="sm:col-span-7">
                        <label className="block text-xs font-bold text-gray-700 mb-1 uppercase">Soin</label>
                        <div className="text-sm font-semibold text-gray-900">
                          {item.soinName || <span className="text-gray-400 italic">Sélectionner un soin</span>}
                        </div>
                      </div>
                      <div className="sm:col-span-3">
                        <label className="flex items-center gap-2 text-xs font-bold text-gray-700 mb-1 uppercase">
                          Prix
                          {Number(item.price) !== Number(item.soinDefaultPrice) && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                              modifié
                            </span>
                          )}
                        </label>
                        {isEditable ? (
                          <input
                            type="number"
                            min="0"
                            step="10"
                            value={item.price}
                            onChange={(e) => handlePriceChange(item.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full border-2 border-indigo-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono font-semibold bg-white"
                            autoFocus
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-bold text-indigo-600">
                              {item.price || 0} DH
                            </div>
                            {Number(item.price) !== Number(item.soinDefaultPrice) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  resetPrice(item.id);
                                }}
                                className="inline-flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all text-lg font-light"
                                title="Réinitialiser au prix par défaut"
                              >
                                ↻
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="sm:col-span-2 flex items-end justify-end gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleValidateLine(item.id);
                          }}
                          className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all ${
                            item.validated
                              ? 'text-green-600 bg-green-100 hover:bg-green-200'
                              : 'text-gray-400 bg-gray-100 hover:text-indigo-600 hover:bg-indigo-100'
                          }`}
                          title={item.validated ? "Dévalider" : "Valider la ligne"}
                        >
                          <CheckCircleIcon className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLine(item.id);
                          }}
                          className="flex items-center justify-center w-9 h-9 text-red-600 bg-red-100 hover:bg-red-200 rounded-lg transition-all"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {teethArray.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {teethArray.map(tooth => (
                          <span
                            key={tooth}
                            className={`inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full transition-all ${
                              !isEditable ? 'opacity-75' : 'hover:bg-indigo-200'
                            }`}
                          >
                            {tooth}
                            {isEditable && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeToothFromLine(item.id, tooth);
                                }}
                                className="hover:text-red-600 transition-colors"
                              >
                                <XMarkIcon className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {items.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 italic">Sélectionnez des dents et un soin pour créer une ligne.</p>
                </div>
              )}
            </div>
            )}

            {/* Status Message */}
            <div className={`mt-4 p-4 rounded-lg text-sm font-medium border-2 flex items-center gap-2 ${
              status.color === 'blue' ? 'bg-blue-50 text-blue-700 border-blue-300' :
              status.color === 'amber' ? 'bg-amber-50 text-amber-700 border-amber-300' :
              'bg-green-50 text-green-700 border-green-300'
            }`}>
              <span className="flex-shrink-0">
                {status.color === 'blue' ? <PencilIcon className="w-5 h-5" /> : status.color === 'amber' ? <ExclamationTriangleIcon className="w-5 h-5" /> : <CheckCircleIcon className="w-5 h-5" />}
              </span>
              {status.message}
            </div>

            {/* Sticky Selected Line List */}
            {activeLineId && (
              <div className="fixed top-6 left-6 bg-white rounded-lg shadow-xl border border-indigo-300 overflow-hidden max-w-xs z-40">
                <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-3 py-2">
                  <h3 className="text-xs font-bold text-white flex items-center gap-2">
                    <DocumentCheckIcon className="w-3 h-3" />
                    Ligne sélectionnée
                  </h3>
                </div>
                <div className="p-3 space-y-2">
                  {(() => {
                    const activeLine = items.find(i => i.id === activeLineId);
                    if (!activeLine) return null;
                    return (
                      <div className="space-y-1.5">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Soin</p>
                          <p className="text-xs font-bold text-gray-900 line-clamp-2">{activeLine.soinName}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Code</p>
                            <p className="text-xs font-mono font-bold text-indigo-600">{activeLine.soinCode}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Prix</p>
                            <p className="text-xs font-bold text-gray-900">{activeLine.price} DH</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setActiveLineId(null);
                            setCurrentTeeth([]);
                          }}
                          className="w-full py-1.5 px-2 bg-gray-100 hover:bg-red-100 text-gray-700 hover:text-red-700 font-semibold text-xs rounded transition-all duration-200"
                        >
                          Désélectionner
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Print Button */}
            <button
              onClick={handlePrint}
              className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-4 rounded-full transition-all duration-300 transform hover:scale-110 shadow-2xl flex items-center justify-center"
              title="Imprimer le devis"
            >
              <PrinterIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Tooth Charts Card */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaceSmileIcon className="w-6 h-6" />
              Sélection des dents
            </h2>
            <div className="relative bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-4 border-2 border-gray-200">
              {/* Adult Tooth Chart */}
              <AdultToothChart
                selectedTeeth={currentTeeth}
                onToothClick={handleToothSelection}
                mode="multiple"
              />
              {/* Children Tooth Chart - properly positioned without overlap */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1/2 z-10 pointer-events-auto">
                <ChildrenToothChart
                  selectedTeeth={currentTeeth}
                  onToothClick={handleToothSelection}
                  mode="multiple"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-6 right-6 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium animate-pulse">
          ✓ {toast}
        </div>
      )}

      {/* Lignes Detail Modal */}
      {isLignesModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="modal-overlay fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={() => setIsLignesModalOpen(false)}></div>
          
          <div className="relative bg-white rounded-xl text-left shadow-2xl transform transition-all sm:w-full sm:max-w-6xl border border-gray-200 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-3 text-white flex-shrink-0 flex items-center justify-between">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <DocumentCheckIcon className="w-5 h-5" />
                Gestion des Lignes ({items.length})
              </h3>
              <button
                onClick={() => setIsLignesModalOpen(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto flex-1 px-4 py-3">
              {items.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 italic">Aucune ligne ajoutée.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedItems.map((item, idx) => {
                    const isEditable = item.id === activeLineId && !item.validated;
                    const teethArray = item.toothCode
                      ? item.toothCode.split(',').map(t => t.trim()).filter(Boolean)
                      : [];

                    return (
                      <div
                        key={item.id}
                        onClick={() => activateLine(item.id)}
                        className={`p-3 rounded border-2 cursor-pointer transition-all duration-200 ${
                          item.validated
                            ? 'bg-green-50 border-green-300'
                            : isEditable
                            ? 'bg-indigo-50 border-indigo-400 shadow-md'
                            : 'bg-gray-50 border-gray-200 hover:border-indigo-300'
                        }`}
                      >
                        <div className="grid grid-cols-12 gap-2 items-center">
                          {/* Row number */}
                          <div className="col-span-1">
                            <span className="text-xs font-bold text-gray-500">{idx + 1}</span>
                          </div>

                          {/* Code */}
                          <div className="col-span-2">
                            <div className="text-xs font-semibold text-gray-900 truncate">
                              {item.soinCode || '—'}
                            </div>
                          </div>

                          {/* Name */}
                          <div className="col-span-4">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {item.soinName || 'N/A'}
                            </div>
                          </div>

                          {/* Teeth */}
                          <div className="col-span-2">
                            <div className="flex flex-wrap gap-1">
                              {teethArray.length > 0 ? (
                                teethArray.map(tooth => (
                                  <span
                                    key={tooth}
                                    className="inline-flex items-center px-2 py-0.5 bg-indigo-200 text-indigo-700 text-xs font-bold rounded"
                                  >
                                    {tooth}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </div>
                          </div>

                          {/* Price */}
                          <div className="col-span-2">
                            {isEditable ? (
                              <input
                                type="number"
                                min="0"
                                step="10"
                                value={item.price}
                                onChange={(e) => handlePriceChange(item.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full border-2 border-indigo-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono font-semibold bg-white"
                                autoFocus
                              />
                            ) : (
                              <div className="flex items-center gap-1">
                                <span className="text-xs font-bold text-indigo-600">
                                  {item.price || 0} DH
                                </span>
                                {Number(item.price) !== Number(item.soinDefaultPrice) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      resetPrice(item.id);
                                    }}
                                    className="text-gray-500 hover:text-gray-700 text-xs"
                                    title="Réinitialiser"
                                  >
                                    ↻
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="col-span-1 flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleValidateLine(item.id);
                              }}
                              className={`flex items-center justify-center w-7 h-7 rounded transition-all flex-shrink-0 ${
                                item.validated
                                  ? 'text-green-600 bg-green-100 hover:bg-green-200'
                                  : 'text-gray-400 bg-gray-100 hover:text-indigo-600 hover:bg-indigo-100'
                              }`}
                              title={item.validated ? "Dévalider" : "Valider"}
                            >
                              <CheckCircleIcon className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeLine(item.id);
                              }}
                              className="flex items-center justify-center w-7 h-7 text-red-600 bg-red-100 hover:bg-red-200 rounded transition-all flex-shrink-0"
                              title="Supprimer"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 flex-shrink-0 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setIsLignesModalOpen(false)}
                className="inline-flex justify-center items-center gap-2 rounded px-3 py-1.5 bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-all"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="modal-overlay fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={() => setIsModalOpen(false)}></div>

          <div className="print-modal relative bg-white rounded-xl text-left shadow-2xl transform transition-all sm:w-full sm:max-w-4xl border border-gray-200 max-h-[95vh] flex flex-col">
            <div className="print-modal-header bg-gradient-to-r from-indigo-600 to-blue-600 px-4 py-4 sm:px-6 text-white flex-shrink-0">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Aperçu du devis</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="print-modal-container bg-white px-4 py-4 sm:px-6 flex-1 overflow-y-auto">
              <DevisDocument
                patientName={patientName}
                date={date}
                items={items}
                discountAmount={discountAmount}
                totalAfterDiscount={totalAfterDiscount}
                showTeethDiagram={false}
              />
            </div>

            <div className="print-modal-footer bg-gray-50 px-4 py-4 sm:px-6 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-200 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  const originalTitle = document.title;
                  const filename = generatePdfFilename();
                  document.title = filename.replace(".pdf", "");
                  window.print();
                  setTimeout(() => {
                    document.title = originalTitle;
                  }, 500);
                }}
                className="w-full sm:w-auto inline-flex justify-center items-center gap-2 rounded-lg px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold hover:from-indigo-700 hover:to-blue-700 transition-all shadow-md"
              >
                <PrinterIcon className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-full sm:w-auto inline-flex justify-center items-center gap-2 rounded-lg border-2 border-gray-300 px-4 py-2.5 bg-white text-gray-700 font-semibold hover:bg-gray-50 transition-all"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevisGenerator;